import CustomLinkWithArrow from '@/_application/core/components/commons/custom-link-with-arrow';
import {ProductCard} from '@/_application/modules/products/components/cards/product-card';
import {Product} from '@/_application/modules/products/model/product';
import ProductAttributesSwiper from '@/_application/pages/home/components/product-category-spotlight/product-attributes-swiper.desktop';
import ProductCategorySpotlightBanner from '@/_application/pages/home/components/product-category-spotlight/product-category-spotlight-banner.desktop';
import ProductCategorySpotlightBrand from '@/_application/pages/home/components/product-category-spotlight/product-category-spotlight-brand.desktop';
import ProductCategorySpotlightHeader from '@/_application/pages/home/components/product-category-spotlight/product-category-spotlight-header.desktop';
import {quantitativeAttributes} from '@/framework/static/filter-attribute-code-value-type';
import {AggregationOption, CategoryTree, HomepageFeaturedBlock, HighlightBrands} from '__generated__/__types__';
import {useTranslation} from 'next-i18next';
import {LazyLoadBlock} from '@/_application/core/components/commons/lazy-load-block';

interface ProductCategorySpotlightProps {
    data: HomepageFeaturedBlock & {
        fetchedProducts?: Product[];
        fetchedHighlightProduct?: Product | null;
    };
    highlightBrands?: HighlightBrands | null;
}

const ProductCategorySpotlight = ({data, highlightBrands}: ProductCategorySpotlightProps) => {
    const {t} = useTranslation('common');

    // Use pre-fetched products from server
    const products = data?.fetchedProducts ?? [];
    const highlightProduct = data?.fetchedHighlightProduct ?? null;

    const brandOptions = (data?.highlight_attribute?.options?.filter(option => option) ?? []) as AggregationOption[];

    // Check type of attribute to show on the left or the right side
    const isQuantitativeAttribute = quantitativeAttributes.includes(data?.product_attributes?.at(0)?.attribute_code!);
    const quantitativeAttributeIndex = isQuantitativeAttribute ? 0 : 1;
    const descriptiveAttributeIndex = isQuantitativeAttribute ? 1 : 0;

    const canonicalUrl = data?.category_items?.[0]?.canonical_url;

    if (data?.category_items?.length === 0) {
        return null;
    }

    return (
        <div className="rounded-[20px] bg-white px-[22px] pb-5 pt-5">
            <ProductCategorySpotlightHeader
                name={data?.category_items?.[0]?.name ?? ''}
                categories={(data?.category_items as CategoryTree[]) ?? []}
            />

            <div className="mb-5 border-b border-KPZ_divider pb-5">
                <ProductCategorySpotlightBanner data={data} />
            </div>

            {data?.product_attributes && data?.product_attributes?.length > 0 && (
                <div className="mb-10 grid grid-cols-1 gap-5 border-b border-KPZ_divider pb-5 lg:grid-cols-2">
                    <div className="border-b pb-5 lg:border-b-0 lg:border-r lg:pb-0">
                        <ProductAttributesSwiper
                            data={data}
                            attributeIndex={descriptiveAttributeIndex}
                            canonicalUrl={canonicalUrl!}
                            isQuantitativeAttribute={false}
                        />
                    </div>

                    <div>
                        <ProductAttributesSwiper
                            data={data}
                            attributeIndex={quantitativeAttributeIndex}
                            canonicalUrl={canonicalUrl!}
                            isQuantitativeAttribute={true}
                        />
                    </div>
                </div>
            )}

            {brandOptions.length > 0 && (
                <ProductCategorySpotlightBrand options={brandOptions} highlightBrands={highlightBrands} />
            )}

            <div className="grid grid-cols-3 gap-5 lg:grid-cols-4 xl:grid-cols-5">
                {/* Highlight product */}
                {highlightProduct && (
                    <div className="col-span-3 lg:col-span-2">
                        <LazyLoadBlock isLazyLoad={true} className="h-full">
                            <ProductCard
                                product={highlightProduct}
                                extraIcon={data?.highlight_item?.icon!}
                                extraText={data?.highlight_item?.label!}
                                variant={'horizontal'}
                                size={'large'}
                            />
                        </LazyLoadBlock>
                    </div>
                )}
                {products.slice(0, 8).map((item: Product) => (
                    <LazyLoadBlock isLazyLoad={true} key={item?.sku}>
                        <ProductCard key={item?.sku} product={item} />
                    </LazyLoadBlock>
                ))}
            </div>

            <div className="mt-5 flex w-full items-center justify-center">
                <CustomLinkWithArrow
                    text={t('text-view-all', 'Xem tất cả')}
                    href={(data?.category_items?.[0]?.canonical_url as string) ?? '#'}
                />
            </div>
        </div>
    );
};

export default ProductCategorySpotlight;
